import type {AutomatedComment, Thread} from '../../conversation-types'
import type {LineKey} from '../../types'
import {useDismissAutomatedReviewThreadMutation} from '@github-ui/pull-requests/use-dismiss-automated-review-thread-mutation'
import {useReopenAutomatedReviewThreadMutation} from '@github-ui/pull-requests/use-reopen-automated-review-thread-mutation'
import {BaseAutomatedReviewCommentActions} from '@github-ui/automated-review-comment-actions/base-automated-review-comment-actions'

export function InlineAutomatedReviewThreadActions({
  automatedComment,
  thread,
  filePath,
  lineMarkersKey,
  setErrorMessage,
  setIsCollapsed,
  safeLocalStorage,
}: {
  automatedComment: AutomatedComment
  thread: Thread
  filePath: string
  lineMarkersKey: LineKey
  setErrorMessage: (message: string) => void
  setIsCollapsed: (isCollapsed: boolean) => void
  safeLocalStorage: {removeItem: (key: string) => void}
}) {
  const {
    mutate: dismissAutomatedReviewThreadMutation,
    isPending: dismissIsPending,
    error: dismissError,
  } = useDismissAutomatedReviewThreadMutation(filePath, lineMarkersKey)

  const handleDismissAutomatedReviewThread = (
    {reason, resolutionNote}: {reason: string; resolutionNote?: string},
    {onSuccess}: {onSuccess: () => void},
  ) => {
    dismissAutomatedReviewThreadMutation(
      {automatedReviewCommentId: automatedComment.id, threadId: thread.id, reason, resolutionNote},
      {
        onError: () => setErrorMessage(`Failed to dismiss`),
        onSuccess: () => {
          onSuccess()
          setIsCollapsed(true)
          safeLocalStorage.removeItem(`reviewThreadIsCollapsed_${thread.id}`)
        },
      },
    )
  }

  const {mutate: reopenAutomatedReviewThreadMutation, isPending: reopenIsPending} =
    useReopenAutomatedReviewThreadMutation(filePath, lineMarkersKey)
  const handleReopenAutomatedReviewThread = () => {
    reopenAutomatedReviewThreadMutation(
      {automatedReviewCommentId: automatedComment.id, threadId: thread.id},
      {
        onError: () => setErrorMessage(`Failed to reopen`),
        onSuccess: () => {
          setIsCollapsed(false)
          safeLocalStorage.removeItem(`reviewThreadIsCollapsed_${thread.id}`)
        },
      },
    )
  }

  return (
    <BaseAutomatedReviewCommentActions
      automatedComment={automatedComment}
      dismissError={dismissError}
      isButtonDisabled={dismissIsPending || reopenIsPending}
      onDismiss={handleDismissAutomatedReviewThread}
      onReopen={handleReopenAutomatedReviewThread}
      className="p-2 border-top"
    />
  )
}

try{ InlineAutomatedReviewThreadActions.displayName ||= 'InlineAutomatedReviewThreadActions' } catch {}